import { useId, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'

import { usePatientDialog } from '../../patients/use-patient-dialog'
import type { BookingChecksRepository } from '../../booking-checks/booking-checks-repository'
import {
  bookingCheckForAppointment,
  useBookingChecks,
  useResolveBookingCheck,
} from '../../booking-checks/use-booking-checks'
import type { PatientRecord } from '../../patients/patients-contracts'
import type { AppointmentAction } from '../appointment-actions-contracts'
import type { AppointmentActionsRepository } from '../appointment-actions-repository'
import { appointmentChangeAvailability } from '../appointment-change-availability'
import type { AppointmentItem } from '../appointments-model'
import styles from '../AppointmentsPage.module.css'
import { useAppointmentAction } from '../use-appointment-action'
import type { BookingCapability } from '../use-booking-capability'
import { AppointmentDetail } from './AppointmentDetail'

/** The verified result shown at once, before the synced record catches up. */
function withActionResult(
  appointment: AppointmentItem,
  action: AppointmentAction
): AppointmentItem {
  return action === 'confirm'
    ? { ...appointment, status: 'confirmed', statusLabel: 'Confirmed' }
    : { ...appointment, status: 'cancelled', statusLabel: 'Cancelled' }
}

export function AppointmentDetailModal({
  appointment,
  tenantId,
  actionRepository,
  bookingCapability,
  patients = [],
  nowMs,
  onNavigate = () => undefined,
  onClose,
  bookingChecksRepository = null,
}: {
  appointment: AppointmentItem
  tenantId: string
  actionRepository?: AppointmentActionsRepository
  bookingCapability?: BookingCapability
  patients?: PatientRecord[]
  nowMs: number
  onNavigate?: (path: string) => void
  onClose: () => void
  bookingChecksRepository?: BookingChecksRepository | null
}) {
  const bookingChecks = useBookingChecks(bookingChecksRepository, tenantId)
  const resolveCheck = useResolveBookingCheck(bookingChecksRepository, tenantId)
  const bookingCheck = bookingCheckForAppointment(
    bookingChecks.data?.items,
    appointment.appointmentId
  )
  const unavailableActions = useMemo<AppointmentActionsRepository>(
    () => ({ execute: () => Promise.reject(new Error('Unavailable')) }),
    []
  )
  const appointmentAction = useAppointmentAction(
    actionRepository ?? unavailableActions,
    tenantId,
    appointment.appointmentId
  )
  const state = appointmentAction.state
  const dialog = useRef<HTMLElement>(null)
  const titleId = useId()
  usePatientDialog(dialog, {
    onClose,
    locked: state.status === 'submitting',
  })
  const patient = appointment.patientAuthorityId
    ? patients.find(
        (item) => item.patientAuthorityId === appointment.patientAuthorityId
      )
    : undefined
  const shown =
    state.status === 'succeeded'
      ? withActionResult(appointment, state.action)
      : appointment

  return createPortal(
    <div className={styles.modalBackdrop} role="presentation">
      <section
        ref={dialog}
        className={styles.detailModal}
        role="dialog"
        aria-modal="true"
        aria-label="Appointment detail"
      >
        <AppointmentDetail
          appointment={shown}
          titleId={titleId}
          availability={appointmentChangeAvailability(
            appointment,
            bookingCapability,
            Boolean(actionRepository)
          )}
          actionState={state}
          nowMs={nowMs}
          onAction={(action) => void appointmentAction.execute(action)}
          onClose={onClose}
          bookingCheck={bookingCheck}
          onResolveBookingCheck={
            bookingCheck && bookingCheck.status === 'OPEN'
              ? (resolutionType) =>
                  resolveCheck.mutate({ check: bookingCheck, resolutionType })
              : undefined
          }
          bookingCheckPending={
            resolveCheck.isPending
              ? (resolveCheck.variables?.resolutionType ?? null)
              : null
          }
          bookingCheckFailed={resolveCheck.isError}
          onOpenPatient={
            patient
              ? () =>
                  onNavigate(
                    `/patients/${encodeURIComponent(patient.patientAuthorityId)}`
                  )
              : undefined
          }
          onOpenConversation={
            patient?.interactionAggregateId
              ? () =>
                  onNavigate(
                    `/conversations?aggregate=${encodeURIComponent(patient.interactionAggregateId!)}`
                  )
              : undefined
          }
        />
      </section>
    </div>,
    document.body
  )
}
